import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Queue, Spinner, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { dmsApi } from './dmsApi';

/**
 * IngestQueueCard — 取り込みの読み取り待ち行列（管理者）。
 *
 * 詰まった仕事を再起動なしで外せるようにするのが目的。処理中の取り消しは
 * 実行中の asyncio タスクを止めるので、枠がすぐ空いて次の人に回る。
 */
export default function IngestQueueCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try { setData(await dmsApi.ingestQueueAdmin()); } catch { setData(null); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    timer.current = setTimeout(load, 4000);
    return () => clearTimeout(timer.current);
  }, [data, load]);

  const cancel = async (job) => {
    setBusy(job.id);
    try {
      await dmsApi.ingestQueueCancel(job.id);
      toast.success(`${job.filename || 'ファイル'} の読み取りを取り消しました（再試行できます）`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '取り消しに失敗しました'); }
    finally { setBusy(null); }
  };

  const cancelAll = async (includeRunning) => {
    if (!window.confirm(includeRunning
      ? '処理中を含めて、待ち行列のすべてを取り消します。ファイルは残り、あとから再読み取りできます。'
      : '待機中のすべてを取り消します。ファイルは残り、あとから再読み取りできます。')) return;
    setBusy('all');
    try {
      const r = await dmsApi.ingestQueueCancelAll({ include_running: includeRunning });
      toast.success(`${r.cancelled}件を取り消しました`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '取り消しに失敗しました'); }
    finally { setBusy(null); }
  };

  const rows = data?.rows || [];

  return (
    <div className="rounded-xl border border-zinc-200 bg-white shadow-sm p-4" data-testid="dms-admin-queue-card">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <p className="text-sm font-bold text-zinc-800 flex items-center gap-1.5">
          <Queue size={16} weight="duotone" className="text-sky-600" /> 読み取り待ち行列（ライブ）
        </p>
        {data && (
          <span className="text-[11px] text-zinc-500" data-testid="dms-admin-queue-stats">
            処理中 {data.running} / 待機 {data.queued} / {data.users_waiting}人 ·
            同時 {data.max_concurrent}件（1人1件）
          </span>
        )}
        {rows.length > 0 && (
          <span className="ml-auto flex gap-1.5">
            <Button size="sm" variant="outline" disabled={busy === 'all'}
              onClick={() => cancelAll(false)} className="h-8 text-xs"
              data-testid="dms-admin-queue-cancel-queued">待機をすべて取消</Button>
            <Button size="sm" variant="outline" disabled={busy === 'all'}
              onClick={() => cancelAll(true)} className="h-8 text-xs text-rose-600 border-rose-300"
              data-testid="dms-admin-queue-cancel-all">処理中も含めて取消</Button>
          </span>
        )}
      </div>
      <p className="text-[11px] text-zinc-400 mb-3">
        取り消してもファイルは消えません（取り込み画面から「もう一度読み取る」で再開できます）。
      </p>

      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-zinc-400" data-testid="dms-admin-queue-empty">
          いま待っている読み取りはありません
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-200">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-zinc-50 text-zinc-500">
                <th className="text-left px-2 py-1.5 w-12">順番</th>
                <th className="text-left px-2 py-1.5">ファイル</th>
                <th className="text-left px-2 py-1.5 w-32">依頼者</th>
                <th className="text-left px-2 py-1.5 w-20">状態</th>
                <th className="text-right px-2 py-1.5 w-20">待ち時間</th>
                <th className="w-16" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-zinc-100" data-testid={`dms-admin-queue-row-${r.id}`}>
                  <td className="px-2 py-1.5 tabular-nums text-zinc-500">
                    {r.status === 'running' ? '—' : r.position}
                  </td>
                  <td className="px-2 py-1.5 truncate max-w-64 text-zinc-800">
                    {r.filename || r.item_id}
                    {r.test_mode && (
                      <span className="ml-1.5 rounded bg-amber-100 px-1 py-0.5 text-[9px] font-semibold text-amber-800"
                        data-testid={`dms-admin-queue-testmode-${r.id}`}>テストモード</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 truncate text-zinc-600"><span data-no-translate>{r.user_name}</span></td>
                  <td className="px-2 py-1.5">
                    {r.status === 'running' ? (
                      <span className="inline-flex items-center gap-1 text-sky-700 font-semibold">
                        <Spinner size={10} className="animate-spin" /> 処理中
                      </span>
                    ) : <span className="text-zinc-500">待機</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-zinc-500">
                    {r.waited_min == null ? '—' : `${r.waited_min}分`}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <Button size="sm" variant="ghost" disabled={busy === r.id}
                      onClick={() => cancel(r)}
                      className="h-7 px-2 text-[11px] text-rose-600 hover:bg-rose-50"
                      data-testid={`dms-admin-queue-cancel-${r.id}`}>
                      <X size={11} className="mr-0.5" /> 取消
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
